import { BaseApi, HttpMethodEnum } from '../BaseApi/BaseApi';
import { IClinicalAlert, IAlertDetail, IAlertSummary, IPatientNotification } from '../../entities/Alert/Alert';

export interface IGetAlertsParams {
    status?: string;
    severity?: string;
    patientId?: number;
    facilityId?: number;
}

export interface IResolveAlertRequest {
    notes: string;
}

export interface IAddCommentRequest {
    comment: string;
}

export interface INotifyPatientRequest {
    message: string;
}

export interface INotifyAssistantRequest {
    message: string;
}

export class AlertApi extends BaseApi {
    private static baseApiUrl: string = `${BaseApi.apiUrl}/Alerts`;

    public static apiUrls = {
        alerts: `${AlertApi.baseApiUrl}`,
        summary: `${AlertApi.baseApiUrl}/Summary`,
        patientAlerts: `${AlertApi.baseApiUrl}/Patient`,
    };

    public static async getAlerts(params?: IGetAlertsParams): Promise<IClinicalAlert[]> {
        return this.sendApiRequest<IClinicalAlert[]>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.alerts,
            data: params as any,
        });
    }

    public static async getAlertDetail(id: number): Promise<IAlertDetail> {
        return this.sendApiRequest<IAlertDetail>({
            method: HttpMethodEnum.GET,
            path: `${this.apiUrls.alerts}/${id}`,
        });
    }

    public static async acknowledgeAlert(id: number): Promise<IClinicalAlert> {
        return this.sendApiRequest<IClinicalAlert>({
            method: HttpMethodEnum.PUT,
            path: `${this.apiUrls.alerts}/${id}/Acknowledge`,
        });
    }

    public static async resolveAlert(id: number, notes: string): Promise<IClinicalAlert> {
        const request: IResolveAlertRequest = { notes };
        return this.sendApiRequest<IClinicalAlert>({
            method: HttpMethodEnum.PUT,
            path: `${this.apiUrls.alerts}/${id}/Resolve`,
            data: request as any,
        });
    }

    public static async addComment(id: number, comment: string): Promise<void> {
        const request: IAddCommentRequest = { comment };
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.alerts}/${id}/Comments`,
            data: request as any,
        });
    }

    public static async notifyPatient(id: number, message: string): Promise<void> {
        const request: INotifyPatientRequest = { message };
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.alerts}/${id}/NotifyPatient`,
            data: request as any,
        });
    }

    public static async notifyAssistant(id: number, message: string): Promise<void> {
        const request: INotifyAssistantRequest = { message };
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.alerts}/${id}/NotifyAssistant`,
            data: request as any,
        });
    }

    public static async visitAlert(id: number): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.alerts}/${id}/Visit`,
        });
    }

    public static async getAlertSummary(): Promise<IAlertSummary> {
        return this.sendApiRequest<IAlertSummary>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.summary,
        });
    }

    public static async visitAllPatientAlerts(): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.patientAlerts}/VisitAll`,
        });
    }

    public static async getPatientAlerts(): Promise<IClinicalAlert[]> {
        return this.sendApiRequest<IClinicalAlert[]>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.patientAlerts,
        });
    }

    public static async getPatientAlertNotifications(alertId: number): Promise<IPatientNotification[]> {
        return this.sendApiRequest<IPatientNotification[]>({
            method: HttpMethodEnum.GET,
            path: `${this.apiUrls.patientAlerts}/${alertId}/Notifications`,
        });
    }

    public static async sendToAssistant(id: number, language: string): Promise<{ assistantName: string; assistantEmail: string }> {
        return this.sendApiRequest<{ assistantName: string; assistantEmail: string }>({
            method: HttpMethodEnum.POST,
            path: `${this.apiUrls.alerts}/${id}/SendToAssistant?language=${language}`,
        });
    }

    public static async downloadAlertPdf(id: number, language: string): Promise<Blob> {
        return this.sendApiRequest<Blob>({
            method: HttpMethodEnum.GET,
            path: `${this.apiUrls.alerts}/${id}/pdf?language=${language}`,
            extraOptionsConfig: { responseType: 'blob' },
        });
    }

    public static downloadPdfFile(pdfBlob: Blob, filename?: string): void {
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || `Alert_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
}
